/**
 * The picture map (spec 026): every picture a story needs, who or what it
 * shows, and where it is used. Pure and deterministic. The stored map holds
 * only what scene content cannot tell (kind, subject, variant, brief, why,
 * `mediaId`); the places a picture is used are derived from the scenes on
 * every read (D-1), by name (D-2).
 */
import type {
  Background, CharacterFraming, Dialog, PictureKind, PictureMap, PictureSlot, SlotUse, Sprite, SpritePictureKind, StoredPictureMap,
  StoredPictureSlot,
} from '../@types';
import { STAGE_SIZES } from '../utils/stageSizes';
import { backgroundStates } from '../utils/backgroundStates';

/** The default background name (`createDefaultBackground`): it never draws a placeholder (D-11). */
export const DEFAULT_BACKGROUND_NAME = 'Background';

/**
 * A name compared for identity: NFKD, accents and apostrophes dropped, lower
 * case, every run of other non-alphanumerics a `-`, trimmed of `-`.
 */
export function pictureSlug(text: string | undefined | null): string {
  return String(text ?? '')
    .normalize('NFKD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/['’‘`´ʼ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

/** `<kind>:<slug(subject)>`, plus `:<slug(variant)>` when the variant is not empty. */
export function pictureSlotId(kind: PictureKind, subject: string, variant?: string): string {
  const v = pictureSlug(variant ?? '');
  return `${kind}:${pictureSlug(subject)}${v ? `:${v}` : ''}`;
}

/** `<subject> · <variant>`, or the subject alone when the variant is empty. */
export function pictureSlotLabel(slot: Pick<PictureSlot, 'subject' | 'variant'>): string {
  const v = (slot.variant ?? '').trim();
  return v ? `${slot.subject} · ${v}` : slot.subject;
}

/**
 * The label a background placeholder shows: its name, when it has no base
 * image and no layers and its name is set and not the default (D-11).
 * Undefined when it draws no placeholder.
 */
export function backgroundPlaceholderName(bg: Partial<Background> | null | undefined): string | undefined {
  if (!bg) return undefined;
  if (typeof bg.baseMediaId === 'number' && bg.baseMediaId > 0) return undefined;
  if ((bg.layers ?? []).length > 0) return undefined;
  const name = (bg.name ?? '').trim();
  if (!name || name === DEFAULT_BACKGROUND_NAME) return undefined;
  return name;
}

/**
 * Whether a background is a slot of the map (D-21): its name is set and not
 * the default, and it has a base image or draws a placeholder (D-11).
 */
export function isSlotBackground(bg: Partial<Background> | null | undefined): boolean {
  if (!bg) return false;
  const name = (bg.name ?? '').trim();
  if (!name || name === DEFAULT_BACKGROUND_NAME || !pictureSlug(name)) return false;
  if (typeof bg.baseMediaId === 'number' && bg.baseMediaId > 0) return true;
  // A named background with states has a slot per state (spec 039 D-6).
  if (backgroundStates(bg).length > 0) return true;
  return backgroundPlaceholderName(bg) !== undefined;
}

/** One line of a scene, numbered from 1 across the scene in play order. */
export interface SceneLineRef {
  dialogId: string;
  lineId: string;
  line: number;
}

/** Dialogs in play order: by start time, then as authored. */
export function dialogsInPlayOrder<D extends Pick<Dialog, 'startTime'>>(dialogs: readonly D[]): D[] {
  return dialogs
    .map((d, i) => ({ d, i }))
    .sort((a, b) => (a.d.startTime ?? 0) - (b.d.startTime ?? 0) || a.i - b.i)
    .map(({ d }) => d);
}

/** Every line of the scene in play order (dialogs by start time, then authored order; lines in order). */
export function sceneLinesInPlayOrder(scene: { dialogs?: readonly Dialog[] | null }): SceneLineRef[] {
  const out: SceneLineRef[] = [];
  for (const d of dialogsInPlayOrder(scene.dialogs ?? [])) {
    for (const l of d.lines ?? []) out.push({ dialogId: d.id, lineId: l.id, line: out.length + 1 });
  }
  return out;
}

/** A scene as the map reads it: 023's `StoryDocScene` shape (key, name, the 021 document). */
export interface PictureScene {
  key: string;
  name: string;
  doc: {
    name?: string;
    background?: Partial<Background> | null;
    sprites?: readonly Sprite[] | null;
    dialogs?: readonly Dialog[] | null;
  };
}

const sceneSprites = (s: PictureScene): readonly Sprite[] => s.doc.sprites ?? [];

const pictureOrZero = (id: unknown): number => (typeof id === 'number' && id > 0 ? id : 0);
const withPicture = <U extends SlotUse>(use: U, id: number): U => (id > 0 ? { ...use, mediaId: id } : use);

/** The uses of a character (or other) picture: sprites named like the subject with a state named like the variant, and the lines set to it. */
function spriteSlotUses(subjectSlug: string, variantSlug: string, scenes: readonly PictureScene[]): SlotUse[] {
  const uses: SlotUse[] = [];
  if (!subjectSlug) return uses;
  for (const scene of scenes) {
    const matched = new Map<string, string>(); // spriteId → stored state name
    for (const sprite of sceneSprites(scene)) {
      if (pictureSlug(sprite.name) !== subjectSlug) continue;
      const state = (sprite.states ?? []).find((st) => pictureSlug(st.name) === variantSlug);
      if (!state) continue;
      matched.set(sprite.id, state.name);
      uses.push(withPicture({ kind: 'sprite', scene: scene.key, spriteId: sprite.id, state: state.name }, pictureOrZero(state.mediaId)));
    }
    if (matched.size === 0) continue;
    const dialogs = scene.doc.dialogs ?? [];
    for (const ref of sceneLinesInPlayOrder(scene.doc)) {
      const line = dialogs.find((d) => d.id === ref.dialogId)?.lines.find((l) => l.id === ref.lineId);
      const ex = line?.expression;
      if (!ex) continue;
      const state = matched.get(ex.spriteId);
      if (state === undefined || pictureSlug(ex.state) !== variantSlug) continue;
      uses.push({ kind: 'line', scene: scene.key, spriteId: ex.spriteId, state, dialogId: ref.dialogId, lineId: ref.lineId, line: ref.line });
    }
  }
  return uses;
}

/**
 * The uses of a background picture: the scenes whose background is named
 * like the subject. A background with no states uses the slot with no
 * variant; one with states (spec 039 D-6) uses one slot per state, the
 * variant naming the state, and the use carries `state`.
 */
function backgroundSlotUses(subjectSlug: string, variantSlug: string, scenes: readonly PictureScene[]): SlotUse[] {
  if (!subjectSlug) return [];
  const uses: SlotUse[] = [];
  for (const s of scenes) {
    const bg = s.doc.background;
    if (pictureSlug(bg?.name) !== subjectSlug) continue;
    const states = backgroundStates(bg);
    if (states.length === 0) {
      if (!variantSlug) uses.push(withPicture({ kind: 'background' as const, scene: s.key }, pictureOrZero(bg?.baseMediaId)));
      continue;
    }
    const state = variantSlug ? states.find((st) => pictureSlug(st.name) === variantSlug) : undefined;
    if (state) uses.push(withPicture({ kind: 'background' as const, scene: s.key, state: state.name }, pictureOrZero(state.mediaId)));
  }
  return uses;
}

/**
 * Moves the stored slot of a background that gets its first states (spec
 * 039 D-2, D-6): `background:<name>` becomes `background:<name>:<state>`
 * (the variant the state), keeping its brief, why and picture; merged into
 * a stored slot of that id when there is one (its own notes win where set).
 * The same array when there is nothing to move.
 */
export function moveBackgroundSlotToState(slots: readonly StoredPictureSlot[], backgroundName: string, state: string): StoredPictureSlot[] {
  const from = pictureSlotId('background', backgroundName, '');
  const at = slots.findIndex((s) => s.id === from);
  if (at === -1 || !pictureSlug(state)) return slots as StoredPictureSlot[];
  const old = slots[at];
  const id = pictureSlotId('background', old.subject, state);
  const target = slots.findIndex((s) => s.id === id);
  if (target === -1) {
    return slots.map((s, i) => (i === at ? storedSlotOf({ ...s, id, variant: state }) : s));
  }
  const t = slots[target];
  const merged = storedSlotOf({
    ...t,
    brief: t.brief.trim() ? t.brief : old.brief,
    why: t.why.trim() ? t.why : old.why,
    mediaId: typeof t.mediaId === 'number' && t.mediaId > 0 ? t.mediaId : old.mediaId,
  });
  return slots.filter((_, i) => i !== at).map((s) => (s.id === id ? merged : s));
}

/** The uses of a slot (D-2); each `sprite` and `background` use carries the picture it shows as `mediaId`. */
export function slotUses(slot: Pick<PictureSlot, 'kind' | 'subject' | 'variant'>, scenes: readonly PictureScene[]): SlotUse[] {
  if (slot.kind === 'background') return backgroundSlotUses(pictureSlug(slot.subject), pictureSlug(slot.variant), scenes);
  return spriteSlotUses(pictureSlug(slot.subject), pictureSlug(slot.variant), scenes);
}

/**
 * `filled` and `mediaId` from a slot's uses (D-19). Filled: at least one
 * `sprite` or `background` use, and every one shows a picture. `mediaId`: the
 * stored one when some use shows it or when no use shows any picture; else
 * the first use's picture; else absent. A stored `mediaId` never fills.
 */
function slotState(stored: number | undefined, uses: readonly SlotUse[]): { filled: boolean; mediaId?: number } {
  const placed = uses.filter((u) => u.kind !== 'line');
  const pictures = placed.map((u) => pictureOrZero(u.mediaId));
  const filled = placed.length > 0 && pictures.every((id) => id > 0);
  const hint = pictureOrZero(stored);
  const shown = pictures.filter((id) => id > 0);
  let mediaId: number | undefined;
  if (hint > 0 && (shown.length === 0 || shown.includes(hint))) mediaId = hint;
  else if (shown.length > 0) mediaId = shown[0];
  return mediaId !== undefined ? { filled, mediaId } : { filled };
}

function withUses(slot: StoredPictureSlot, scenes: readonly PictureScene[]): PictureSlot {
  const uses = slotUses(slot, scenes);
  const { mediaId: stored, ...rest } = slot;
  return { ...rest, uses, ...slotState(stored, uses) };
}

/** The identity a sprite slot (character, prop or other) matches sprites by. */
const spriteIdentity = (subject: string, variant: string) => `${pictureSlug(subject)}|${pictureSlug(variant)}`;

/** The four kinds (D-25), in the order the types list them. */
export const PICTURE_KINDS: readonly PictureKind[] = Object.freeze(['character', 'prop', 'background', 'other']);
/** The kinds a sprite's pictures can have (D-26), in the Kind switch's order. */
export const SPRITE_PICTURE_KINDS: readonly SpritePictureKind[] = Object.freeze(['character', 'prop', 'other']);
export const isPictureKind = (v: unknown): v is PictureKind => typeof v === 'string' && (PICTURE_KINDS as readonly string[]).includes(v);
export const isSpritePictureKind = (v: unknown): v is SpritePictureKind =>
  typeof v === 'string' && (SPRITE_PICTURE_KINDS as readonly string[]).includes(v);

/** What Maya reads for a kind (D-29): `other` is "Full picture". */
export const PICTURE_KIND_LABELS: Readonly<Record<PictureKind, string>> = Object.freeze({
  character: 'Character', prop: 'Prop', other: 'Full picture', background: 'Background',
});

/** "expression(s)" for a character, "state(s)" for a prop, "picture(s)" otherwise (D-29). */
export function pictureVariantNoun(kind: PictureKind, count: number): string {
  const one = count === 1;
  if (kind === 'character') return one ? 'expression' : 'expressions';
  if (kind === 'prop') return one ? 'state' : 'states';
  return one ? 'picture' : 'pictures';
}

/** "a character", "a prop", "a full picture" (the summaries and messages of 026's operations). */
export const pictureKindPhrase = (kind: PictureKind): string =>
  `a ${PICTURE_KIND_LABELS[kind].toLowerCase()}`;

/**
 * Whether a sprite fills the stage (D-27): its box (`size`, absent
 * 100 × 100) times its base `transform.scale` covers at least 90% of the
 * width and at least 90% of the height of one orientation's stage.
 */
export function fillsStage(sprite: Pick<Sprite, 'size' | 'transform'> | null | undefined): boolean {
  if (!sprite) return false;
  const scale = typeof sprite.transform?.scale === 'number' && Number.isFinite(sprite.transform.scale) ? Math.abs(sprite.transform.scale) : 1;
  const w = (sprite.size?.width ?? 100) * scale;
  const h = (sprite.size?.height ?? 100) * scale;
  return Object.values(STAGE_SIZES).some((stage) => w >= stage.width * 0.9 && h >= stage.height * 0.9);
}

/** The kind of a subject's first stored sprite slot, in stored order (D-27's first source). */
function storedSpriteKindOf(slots: readonly (StoredPictureSlot | PictureSlot)[], subjectSlug: string): SpritePictureKind | undefined {
  const hit = slots.find((s) => isSpritePictureKind(s.kind) && pictureSlug(s.subject) === subjectSlug);
  return hit ? (hit.kind as SpritePictureKind) : undefined;
}

/**
 * The kind of a subject's sprite pictures (D-26): its first sprite slot's
 * kind in map order (the stored slots come first, so a stored kind wins;
 * implicit slots carry the kind D-27 derived), else `character`.
 */
export function pictureKindOf(map: PictureMap | StoredPictureMap | null | undefined, subject: string): SpritePictureKind {
  return storedSpriteKindOf(map?.slots ?? [], pictureSlug(subject)) ?? 'character';
}

/**
 * The picture map: the stored slots (in stored order) with their uses, then
 * one implicit slot per sprite state (with a picture or without) and per
 * slot background (D-21) that no stored slot matches, ordered by first use
 * (scene by scene: the background, then each sprite's states) (D-18).
 */
export function syncPictureMap(stored: StoredPictureMap | null | undefined, scenes: readonly PictureScene[]): PictureMap {
  const storedSlots = Array.isArray(stored?.slots) ? stored!.slots : [];
  const slots: PictureSlot[] = storedSlots.map((s) => withUses(s, scenes));

  const spriteTaken = new Set(
    storedSlots.filter((s) => s.kind !== 'background').map((s) => spriteIdentity(s.subject, s.variant)),
  );
  // D-27: a subject's kind is its stored slots', else 'other' when its first sprite fills the stage, else 'character'.
  const implicitKind = new Map<string, SpritePictureKind>();
  const kindFor = (sprite: Sprite): SpritePictureKind => {
    const slug = pictureSlug(sprite.name);
    let kind = implicitKind.get(slug);
    if (!kind) {
      kind = storedSpriteKindOf(storedSlots, slug) ?? (fillsStage(sprite) ? 'other' : 'character');
      implicitKind.set(slug, kind);
    }
    return kind;
  };
  // By slot id: a background with states has one slot per state (spec 039 D-6).
  const backgroundTaken = new Set(storedSlots.filter((s) => s.kind === 'background').map((s) => s.id));
  for (const scene of scenes) {
    const bg = scene.doc.background;
    if (isSlotBackground(bg)) {
      const bgName = (bg!.name ?? '').trim();
      const states = backgroundStates(bg);
      const variants = states.length > 0 ? states.map((s) => s.name).filter((n) => pictureSlug(n)) : [''];
      for (const variant of variants) {
        const id = pictureSlotId('background', bgName, variant);
        if (backgroundTaken.has(id)) continue;
        backgroundTaken.add(id);
        slots.push(withUses({ id, kind: 'background', subject: bgName, variant, brief: '', why: '' }, scenes));
      }
    }
    for (const sprite of sceneSprites(scene)) {
      if (!pictureSlug(sprite.name)) continue;
      const kind = kindFor(sprite);
      for (const state of sprite.states ?? []) {
        if (!pictureSlug(state.name)) continue;
        const identity = spriteIdentity(sprite.name, state.name);
        if (spriteTaken.has(identity)) continue;
        spriteTaken.add(identity);
        const id = pictureSlotId(kind, sprite.name, state.name);
        slots.push(withUses({ id, kind, subject: sprite.name, variant: state.name, brief: '', why: '' }, scenes));
      }
    }
  }
  // Every character slot of a subject carries the subject's stored framing (D-24).
  return {
    slots: slots.map((slot) => {
      if (slot.kind !== 'character') return slot;
      const framing = storedFramingOf(storedSlots, slot.subject);
      if (framing) return { ...slot, framing };
      if (slot.framing === undefined) return slot;
      const { framing: _drop, ...rest } = slot;
      return rest;
    }),
  };
}

/**
 * The slots with a use in one scene, as the map would read once that scene's
 * live content is saved (the studio strip, 026): each slot's uses in other
 * scenes are kept (with their `mediaId`), its uses in `scene.key` are
 * re-derived from the live content, a slot only the live scene has is added
 * as an implicit slot, and `filled` and `mediaId` are recomputed (D-19, the
 * slot's current `mediaId` standing in for the stored one).
 */
export function scenePictureSlots(map: PictureMap | null | undefined, scene: PictureScene): PictureSlot[] {
  const mapSlots = map?.slots ?? [];
  const stored: StoredPictureMap = { slots: mapSlots.map((s) => storedSlotOf(s)) };
  const live = syncPictureMap(stored, [scene]);
  const out: PictureSlot[] = [];
  live.slots.forEach((liveSlot, i) => {
    if (liveSlot.uses.length === 0) return;
    const before = i < mapSlots.length ? mapSlots[i] : mapSlots.find((s) => s.id === liveSlot.id);
    const prior = before?.uses ?? [];
    const at = prior.findIndex((u) => u.scene === scene.key);
    const others = prior.filter((u) => u.scene !== scene.key);
    const uses = at === -1
      ? [...others, ...liveSlot.uses]
      : [...prior.slice(0, at).filter((u) => u.scene !== scene.key), ...liveSlot.uses, ...prior.slice(at).filter((u) => u.scene !== scene.key)];
    const { uses: _u, filled: _f, mediaId: _m, ...rest } = liveSlot;
    out.push({ ...rest, uses, ...slotState(before?.mediaId, uses) });
  });
  return out;
}

/** Filled (D-19): every sprite and background use shows a picture; the `mediaId` plays no part. */
export const isSlotFilled = (slot: Pick<PictureSlot, 'filled'>): boolean => slot.filled === true;
/** Needed: not filled and at least one use. */
export const isSlotNeeded = (slot: Pick<PictureSlot, 'filled' | 'uses'>): boolean => !isSlotFilled(slot) && slot.uses.length > 0;
/** The slots views and counts show: those with at least one use. */
export const slotsInUse = (map: PictureMap | null | undefined): PictureSlot[] => (map?.slots ?? []).filter((s) => s.uses.length > 0);

/** The stored slots no scene uses, in stored order (the Pictures view's "Not used in any scene", D-15). */
export const slotsNotInUse = (map: PictureMap | null | undefined): PictureSlot[] => (map?.slots ?? []).filter((s) => s.uses.length === 0);

/** `{total, filled, needed}` over the slots with at least one use. */
export function pictureMapCounts(map: PictureMap | null | undefined): { total: number; filled: number; needed: number } {
  const inUse = slotsInUse(map);
  const filled = inUse.filter(isSlotFilled).length;
  return { total: inUse.length, filled, needed: inUse.length - filled };
}

/** The needed pictures grouped by subject, in map order (024's turn `pictures.needed`, 026's card). */
export function neededPictureGroups(map: PictureMap | null | undefined): { kind: PictureKind; subject: string; variants: string[] }[] {
  const groups: { kind: PictureKind; subject: string; variants: string[]; key: string }[] = [];
  for (const slot of slotsInUse(map)) {
    if (isSlotFilled(slot)) continue;
    const key = `${slot.kind}|${pictureSlug(slot.subject)}`;
    let group = groups.find((g) => g.key === key);
    if (!group) {
      group = { kind: slot.kind, subject: slot.subject, variants: [], key };
      groups.push(group);
    }
    if (slot.variant) group.variants.push(slot.variant);
  }
  return groups.map(({ key: _k, ...g }) => g);
}

/** The stored form of a slot (no `uses`, no `filled`). */
export function storedSlotOf(slot: PictureSlot | StoredPictureSlot): StoredPictureSlot {
  const out: StoredPictureSlot = {
    id: slot.id, kind: slot.kind, subject: slot.subject, variant: slot.variant, brief: slot.brief, why: slot.why,
  };
  if (typeof slot.mediaId === 'number' && slot.mediaId > 0) out.mediaId = slot.mediaId;
  if (slot.kind === 'character' && isCharacterFraming(slot.framing)) out.framing = slot.framing;
  return out;
}

/** The four framings, in the order the Generate panel shows them (027 D-22). */
export const CHARACTER_FRAMINGS: readonly CharacterFraming[] = ['head', 'half', 'knees', 'full'];
export const DEFAULT_FRAMING: CharacterFraming = 'half';
export const FRAMING_LABELS: Readonly<Record<CharacterFraming, string>> = Object.freeze({
  head: 'Head and shoulders', half: 'Half body', knees: 'Knees up', full: 'Full body',
});

export const isCharacterFraming = (v: unknown): v is CharacterFraming =>
  typeof v === 'string' && (CHARACTER_FRAMINGS as readonly string[]).includes(v);

/** The stored framing of a subject's character slots: the first one that has one (D-24). */
function storedFramingOf(slots: readonly (StoredPictureSlot | PictureSlot)[], subject: string): CharacterFraming | undefined {
  const key = pictureSlug(subject);
  const hit = slots.find((s) => s.kind === 'character' && pictureSlug(s.subject) === key && isCharacterFraming(s.framing));
  return hit?.framing;
}

/**
 * A character's or a prop's master picture (027 D-33, D-35, D-37), the one
 * place a character's framing and Reset live and the one picture the other
 * expressions or states are drawn from: its `neutral` slot with a use, else
 * its first slot with a use in map order; else none. A full picture
 * (`other`) and a background have no master (026 D-25).
 */
export function masterSlotOf(map: PictureMap | null | undefined, subject: string): PictureSlot | undefined {
  const key = pictureSlug(subject);
  const mine = (map?.slots ?? []).filter((s) => isSpritePictureKind(s.kind) && pictureSlug(s.subject) === key && s.uses.length > 0);
  const kind = mine[0]?.kind;
  if (kind !== 'character' && kind !== 'prop') return undefined;
  const same = mine.filter((s) => s.kind === kind);
  return same.find((s) => pictureSlug(s.variant) === 'neutral') ?? same[0];
}

/** A subject's framing (026 D-24): its character slots' stored framing, else 'half'. */
export const framingOf = (map: PictureMap | StoredPictureMap | null | undefined, subject: string): CharacterFraming =>
  storedFramingOf(map?.slots ?? [], subject) ?? DEFAULT_FRAMING;

/**
 * A use in Maya's words: `<scene> · line <n>`, `<scene> · default` (a sprite
 * use in its default state), `<scene>` (a background) or `<scene> · not on a
 * line yet`.
 */
export function useLabel(use: SlotUse, sceneName: string, sprite?: Pick<Sprite, 'defaultState'>): string {
  if (use.kind === 'line') return `${sceneName} · line ${use.line}`;
  if (use.kind === 'background') return use.state ? `${sceneName} · ${use.state}` : sceneName;
  if (sprite && use.state === sprite.defaultState) return `${sceneName} · default`;
  return `${sceneName} · not on a line yet`;
}

const sceneNameOf = (s: PictureScene | undefined) => (s ? s.doc.name || s.name : '');

/** One place of a slot (`slotPlaceList`): its words, the use, and whether it shows a picture now. */
export interface SlotPlace {
  label: string;
  use: SlotUse;
  /** A sprite or background use with a picture, or a line whose sprite use in that scene has one. */
  shows: boolean;
}

/**
 * The places a slot is used, leaving out a sprite use whose scene already
 * lists a line use of the slot, each with whether it shows a picture (the
 * Pictures view's " · placeholder", D-19).
 */
export function slotPlaceList(slot: Pick<PictureSlot, 'uses'>, scenes: readonly PictureScene[]): SlotPlace[] {
  const withLines = new Set(slot.uses.filter((u) => u.kind === 'line').map((u) => u.scene));
  const out: SlotPlace[] = [];
  for (const use of slot.uses) {
    if (use.kind === 'sprite' && withLines.has(use.scene)) continue;
    const scene = scenes.find((s) => s.key === use.scene);
    const sprite = use.spriteId ? scene?.doc.sprites?.find((x) => x.id === use.spriteId) : undefined;
    const shows = use.kind === 'line'
      ? slot.uses.some((u) => u.kind === 'sprite' && u.scene === use.scene && u.spriteId === use.spriteId && pictureOrZero(u.mediaId) > 0)
      : pictureOrZero(use.mediaId) > 0;
    out.push({ label: useLabel(use, sceneNameOf(scene), sprite), use, shows });
  }
  return out;
}

/** The places a slot is used, as `useLabel`s (`slotPlaceList`'s labels). Its length is the uses count. */
export function slotPlaces(slot: Pick<PictureSlot, 'uses'>, scenes: readonly PictureScene[]): string[] {
  return slotPlaceList(slot, scenes).map((p) => p.label);
}

/** The `sprite` and `background` uses that show no picture (D-22's "Use the same picture here" targets). */
export const usesWithoutPicture = (slot: Pick<PictureSlot, 'uses'>): SlotUse[] =>
  slot.uses.filter((u) => u.kind !== 'line' && pictureOrZero(u.mediaId) === 0);

/** `{shown, places}`: of the slot's `sprite` and `background` uses, how many show a picture. */
export function slotShownCount(slot: Pick<PictureSlot, 'uses'>): { shown: number; places: number } {
  const placed = slot.uses.filter((u) => u.kind !== 'line');
  return { shown: placed.filter((u) => pictureOrZero(u.mediaId) > 0).length, places: placed.length };
}

/** At most this many slots in a stored map (spec 030). */
export const PICTURE_MAP_MAX_SLOTS = 500;

const isPositiveInt = (v: unknown): v is number => typeof v === 'number' && Number.isInteger(v) && v > 0;

/**
 * Checks a picture map sent to be stored (spec 030, a scene's
 * `PATCH {pictureMap}`): an object whose `slots` is an array of at most 500
 * slots, each with a known `kind`, a `subject` of 1–120 characters with a
 * letter or digit, a `variant` of at most 60 (set for a sprite kind; for a
 * background empty, or the state it shows, spec 039), a `brief` of at most 500 and a `why` of at most 200, an `id`
 * equal to `pictureSlotId(kind, subject, variant)`, an optional positive
 * integer `mediaId` and an optional framing. On success the map as stored
 * (`storedSlotOf`: unknown keys, `uses` and `filled` dropped).
 */
export function validatePictureMap(value: unknown): { ok: true; map: StoredPictureMap } | { ok: false; error: string } {
  const bad = (error: string) => ({ ok: false as const, error });
  if (!value || typeof value !== 'object' || Array.isArray(value)) return bad('pictureMap must be an object with slots');
  const slots = (value as { slots?: unknown }).slots;
  if (!Array.isArray(slots)) return bad('pictureMap.slots must be an array');
  if (slots.length > PICTURE_MAP_MAX_SLOTS) return bad(`pictureMap holds at most ${PICTURE_MAP_MAX_SLOTS} slots`);
  const out: StoredPictureSlot[] = [];
  for (let i = 0; i < slots.length; i++) {
    const s = slots[i] as Record<string, unknown> | null;
    const at = `pictureMap.slots[${i}]`;
    if (!s || typeof s !== 'object' || Array.isArray(s)) return bad(`${at} must be an object`);
    const { kind, subject, variant = '', brief = '', why = '', id, mediaId, framing } = s;
    if (typeof kind !== 'string' || !(PICTURE_KINDS as readonly string[]).includes(kind)) return bad(`${at}.kind must be character, prop, background or other`);
    if (typeof subject !== 'string' || subject.length < 1 || subject.length > 120 || !pictureSlug(subject)) {
      return bad(`${at}.subject must be 1–120 characters with a letter or digit`);
    }
    if (typeof variant !== 'string' || variant.length > 60) return bad(`${at}.variant must be at most 60 characters`);
    // A background's variant is empty, or names one of its states (spec 039 D-6).
    if (kind !== 'background' && !pictureSlug(variant)) {
      return bad(`${at}.variant must be set for a ${kind}`);
    }
    if (typeof brief !== 'string' || brief.length > 500) return bad(`${at}.brief must be at most 500 characters`);
    if (typeof why !== 'string' || why.length > 200) return bad(`${at}.why must be at most 200 characters`);
    const expected = pictureSlotId(kind as PictureKind, subject, variant);
    if (id !== expected) return bad(`${at}.id must be ${expected}`);
    if (mediaId !== undefined && !isPositiveInt(mediaId)) return bad(`${at}.mediaId must be a positive integer`);
    if (framing !== undefined && !isCharacterFraming(framing)) return bad(`${at}.framing must be head, half, knees or full`);
    out.push(storedSlotOf({
      id: expected, kind: kind as PictureKind, subject, variant, brief, why,
      ...(mediaId !== undefined ? { mediaId: mediaId as number } : {}),
      ...(framing !== undefined ? { framing: framing as CharacterFraming } : {}),
    }));
  }
  return { ok: true, map: { slots: out } };
}

/**
 * The sentence both assistants' fixed prompts end their picture rules with
 * (026 D-25 to D-27; the story prompt and 030's scene picture paragraph).
 */
export const PICTURE_KINDS_SENTENCE = 'Not every sprite is a character. Note a person with kind character. Note an object the scene shows (a chest, a door, a phone) with kind prop: name its states for how it looks (closed, open), the default first, and describe only the object in that state. Note a sprite that shows a whole picture with its own background (a full-screen backdrop that fades in, a map, a letter, a cut-away) with kind other: its brief describes the whole picture, setting included, with no text in it.';
